import { useQuery } from '@tanstack/react-query'

import { FxIcon } from '@/components/fx'
import { useUsage } from '@/hooks/useUsage'
import { billingApi } from '@/lib/api/billing'
import { cn } from '@/lib/utils'
import { useClientConfig } from './useClientConfig'

/**
 * The one figure the utility row carries — and it is one figure, not two.
 *
 * The product currently counts two different things and only one of them is
 * enforced anywhere:
 *
 * - **Pages**, `GET /v1/billing/usage`, the free tier. Enforced on upload
 *   when `enforced` is true; `pagesRemaining` is **absent on an unlimited
 *   plan and 0 when none are left**, which must not read the same, so the
 *   two are separate branches below and neither is derived from `maxPages`.
 * - **Credits**, the `balance` beside `GET /v1/billing/packages`. Granted by
 *   a purchase, spent by nothing: `GATEWAY_BILLING_CREDITS_ENFORCED` is off
 *   in every deployment today, so a balance is a stored number, not an
 *   allowance.
 *
 * Rule: the page figure wins where it is enforced, because it is the one
 * that can actually refuse an upload; the balance shows only where there is
 * no page limit to report and the deployment really has a credit store. With
 * neither, this renders **nothing at all** rather than the artboard's
 * "10 350 Guthaben", which would be a number we made up.
 *
 * Before either: `GET /v1/config` says whether this deployment applies plan
 * limits at all (`billing.enabled`). Where it does not — a deployment that
 * only does invoiced work — there is nothing here to report and nothing to
 * ask for, so both queries stay unasked and the row stays empty. That used to
 * be inferred from a usage response that answers either way.
 *
 * It is text, not a link: `/billing` and `/einstellungen/abrechnung` are
 * deferred (no ledger, no prices worth showing), and a chip that navigates
 * to a 404 is worse than a chip that does not navigate.
 */
export function ShellUsageLine({ className }: { className?: string }) {
  const config = useClientConfig()
  const usage = useUsage()

  // `enabled === false` is a fact, not a missing answer: no plan limit
  // applies, so neither figure means anything here. An unanswered config
  // (`undefined`) is not that, and leaves the old behaviour in place.
  const billingOff = config.data?.billing.enabled === false

  const plan = usage.data
  const pages = plan?.usage
  const pagesEnforced =
    plan?.enforced === true && pages != null && pages.maxPages >= 0 && pages.pagesRemaining != null

  // Only asked for when there is no page figure to show: it is a second
  // round trip, and on a deployment that sells nothing it answers with no
  // balance at all.
  const credits = useQuery({
    queryKey: ['billing', 'packages'],
    queryFn: () => billingApi.packages(),
    enabled: usage.isSuccess && !pagesEnforced && !billingOff,
    staleTime: 5 * 60_000,
    retry: false,
  })

  if (billingOff) return null

  if (pagesEnforced && pages) {
    const left = pages.pagesRemaining ?? 0
    return (
      <Chip
        className={className}
        icon="description"
        tone={left === 0 ? 'danger' : 'default'}
        label={
          left === 0
            ? 'Keine Freiseiten mehr'
            : `Noch ${formatNumber(left)} von ${formatNumber(pages.maxPages)} Seiten`
        }
      />
    )
  }

  const balance = credits.data?.balance
  if (typeof balance === 'number') {
    return (
      <Chip
        className={className}
        icon="account_balance_wallet"
        label={`${formatNumber(balance)} Guthaben`}
      />
    )
  }

  return null
}

const numberFormat = new Intl.NumberFormat('de-DE')
function formatNumber(value: number) {
  return numberFormat.format(value)
}

function Chip({
  icon,
  label,
  tone = 'default',
  className,
}: {
  icon: string
  label: string
  tone?: 'default' | 'danger'
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex h-control flex-none items-center gap-2 rounded-md bg-surface px-4 text-small shadow-raised',
        tone === 'danger' ? 'text-danger-text' : 'text-text',
        className,
      )}
    >
      <FxIcon
        name={icon}
        size={20}
        className={tone === 'danger' ? 'text-danger-text' : 'text-primary'}
      />
      {label}
    </span>
  )
}
